import { Component, computed, input, signal, viewChildren } from '@angular/core';
import { beautifyCode } from 'apps/account/src/account-shared/code-mirror/code-mirror.util';
import { FtCodeMirrorComponent } from 'apps/account/src/account-shared/code-mirror/ft-code-mirror.component';

/**
 * Native port of the AngularJS `ftCssField`/`ftJsField` field editors: one code editor per
 * (normalised) field, bound to `datasource[field.name]`; non-empty values are beautified on load.
 *
 * Was Ace (`window.ace`, eagerly loaded into the main bundle plus a 9.6 MB `./ace` asset copy);
 * now CodeMirror 6 through the shared `ft-code-mirror`, which loads on demand.
 */
@Component({
    selector: 'ft-code-field-ng',
    imports: [FtCodeMirrorComponent],
    templateUrl: './ft-code-field.component.html',
    styles: [
        // v1 sized these through the global `.ace_editor { height: 480px }` rule in
        // account-v1/assets/css/components/_editors.css. Nothing sets a height on the wrapper, so
        // the replacement has to carry it or the editor collapses to zero.
        `.ft-code-field_host {
            display: block;
            height: 480px;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            overflow: hidden;
        }`
    ]
})
export class FtCodeFieldComponent {
    /** The object whose code fields the editors edit (e.g. `data.contest`). */
    readonly datasource = input<any>();
    /** A field name, or `{name,label}`, or a list of either — normalised below. */
    readonly fields = input<any>();
    /** Syntax mode: 'css' | 'javascript' | 'json'. */
    readonly syntax = input<'css' | 'javascript' | 'json'>('css');

    protected readonly normalizedFields = computed<Array<{ name: string; label: string | null }>>(() => {
        const fields = this.fields();
        const raw = Array.isArray(fields) ? fields : fields == null ? [] : [fields];
        return raw.map((f: any) => (typeof f === 'string' ? { name: f, label: null } : f));
    });

    /**
     * Syntax-error count across the editors — v1 codeEditor.html kept a hidden `required` checkbox
     * bound `ng-true-value="0"` to `errors.length`, so ANY syntax error flipped the enclosing modal
     * form invalid and dimmed Save. The modal shell reads this.
     *
     * Ace fed it from `session.on('changeAnnotation')`; CodeMirror feeds it from
     * `@codemirror/lint` diagnostics over the Lezer grammar (`countSyntaxErrors`). v1 only counted
     * errors when the editor had content (`scope.errors = scope.code ? … : []`) — that exemption
     * now lives inside `syntaxDiagnostics`, so an empty editor still never blocks Save.
     */
    readonly syntaxErrorCount = signal(0);

    private readonly editors = viewChildren(FtCodeMirrorComponent);

    /**
     * Seed one editor once its view exists. Driven by `(ready)` rather than `ngAfterViewInit`
     * because the editor loads from a lazy chunk — at `ngAfterViewInit` there is no view yet. That
     * also keeps the first error count out of the change-detection pass that mounted the modal,
     * which matches Ace (its annotations arrived from a web worker, always a turn later).
     */
    protected onEditorReady(editor: FtCodeMirrorComponent, fieldName: string): void {
        let value = this.datasource()?.[fieldName];
        value = value == null ? '' : String(value);
        // v1 codeEditor.js aceLoaded: cssBeautify for css, jsBeautify for json/javascript.
        if (value) {
            value = beautifyCode(value, this.syntax());
        }
        editor.setValue(value);
        // Ace's `setValue` fired `change`, so v1 wrote the beautified text straight back to the
        // datasource before the user touched anything. `setValue` here is deliberately silent, so
        // do it explicitly rather than let the two editors disagree about what was saved.
        this.writeBack(fieldName, value);
        this.recountErrors();
    }

    /** A user edit in one of the editors. */
    protected onDocChange(fieldName: string, value: string): void {
        this.writeBack(fieldName, value);
        this.recountErrors();
    }

    private writeBack(fieldName: string, value: string): void {
        const datasource = this.datasource();
        if (datasource) {
            datasource[fieldName] = value;
        }
    }

    private recountErrors(): void {
        this.syntaxErrorCount.set(this.editors().reduce((total, editor) => total + editor.errorCount(), 0));
    }
}
